// Colour helpers ported from www. `generateShadeScale` expands a single brand
// colour (hex or rgba) into an N-step shade ramp, used to feed PrimeVue's
// `semantic.primary` / `semantic.secondary` token scales.

const hexToRgb = (hex: string): [number, number, number] => {
    const bigint = parseInt(hex.slice(1), 16);
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

const rgbToHex = (r: number, g: number, b: number): string => {
    return `#${((1 << 24) | (r << 16) | (g << 8) | b)
        .toString(16)
        .slice(1)
        .toUpperCase()}`;
};

function rgbaStringToArray(rgba: string): number[] {
    const result = rgba.match(/\d+(\.\d+)?/g);

    if (!result || (result.length !== 4 && result.length !== 3)) {
        throw new Error("Invalid RGBA format");
    }

    return result.map((num) => {
        const floatNum = parseFloat(num);
        return Number.isNaN(floatNum) ? 1 : floatNum;
    });
}

function isValidRgba(rgba: string): boolean {
    const rgbaRegex = /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|0?\.\d+|1(\.0)?)\s*\)$/;

    if (!rgbaRegex.test(rgba)) {
        return false;
    }

    const result = rgba.match(/\d+(\.\d+)?/g);
    if (!result || result.length !== 4) {
        return false;
    }

    const [r, g, b, a] = result.map((num) => parseFloat(num));

    return (
        r >= 0 && r <= 255 &&
        g >= 0 && g <= 255 &&
        b >= 0 && b <= 255 &&
        a >= 0 && a <= 1
    );
}

export default function generateShadeScale(color: string, steps: number, position: number): string[] {
    const adjustLightness = (r: number, g: number, b: number, factor: number): [number, number, number] => {
        const adjust = (value: number) => Math.min(255, Math.max(0, Math.round(value * factor)));
        return [adjust(r), adjust(g), adjust(b)];
    };

    if (position < 1 || position > steps) {
        throw new Error("Position must be between 1 and the total number of steps.");
    }

    const rgb = isValidRgba(color) ? rgbaStringToArray(color) : hexToRgb(color);
    const scale: string[] = [];
    const totalLighter = position - 1;
    const totalDarker = steps - position;

    for (let i = totalLighter; i > 0; i--) {
        const factor = 1 + (i / totalLighter) * 0.5;
        const [r, g, b] = adjustLightness(rgb[0], rgb[1], rgb[2], factor);
        scale.unshift(rgbToHex(r, g, b));
    }

    scale.push(color);

    for (let i = 1; i <= totalDarker; i++) {
        const factor = 1 - (i / totalDarker) * 0.5;
        const [r, g, b] = adjustLightness(rgb[0], rgb[1], rgb[2], factor);
        scale.push(rgbToHex(r, g, b));
    }

    return scale;
}
